import { useState } from "react"
import { Box, Button, Dialog, DialogContent, IconButton, Skeleton, Stack, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined"
import BrokenImageIcon from "@mui/icons-material/BrokenImageOutlined"

import { useObjectUrl } from "./useObjectUrl"
import { downloadFile, fileUrl } from "../../api/uploads"

/**
 * An image attachment, shown inline with a click-to-enlarge dialog.
 *
 * Only ever rendered for a file the SERVER marked `inline` — that flag comes
 * from the same allow-list the download route serves by, so the client cannot
 * decide on its own to render something as an image. An SVG, notably, never
 * reaches here.
 */
export default function ImageAttachment({ file, maxHeight = 260 }) {
  const [open, setOpen] = useState(false)
  const path = fileUrl(file.client_id, file.file_id ?? file.id)
  const { url, error, loading, retry } = useObjectUrl(path)

  if (error) {
    return (
      <Box sx={{ p: 1.5, borderRadius: 1.5, border: 1, borderColor: "divider", maxWidth: 320 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <BrokenImageIcon fontSize="small" sx={{ color: "text.disabled" }} />
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {error.message}
          </Typography>
          {/* Only where asking again could work. A 404 is the server's single
              answer for "gone" and "not yours", and retrying either is a button
              that does nothing twice. */}
          {error.retryable ? (
            <Button size="small" onClick={retry} sx={{ flexShrink: 0 }}>
              Retry
            </Button>
          ) : null}
        </Stack>
      </Box>
    )
  }

  if (loading || !url) {
    // A fixed-height skeleton rather than a spinner: the layout does not jump
    // when the image lands, which matters in a thread that is scrolled to the
    // bottom.
    return <Skeleton variant="rounded" width={240} height={160} />
  }

  return (
    <>
      <Box
        component="img"
        src={url}
        alt={file.file_name}
        loading="lazy"
        onClick={() => setOpen(true)}
        sx={{
          display: "block",
          maxWidth: "100%",
          maxHeight,
          borderRadius: 1.5,
          border: 1,
          borderColor: "divider",
          cursor: "zoom-in",
        }}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg">
        <DialogContent sx={{ p: 0, position: "relative", bgcolor: "black", lineHeight: 0 }}>
          <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 0.5, zIndex: 1 }}>
            <IconButton
              size="small"
              onClick={() => downloadFile(file.client_id, file.file_id ?? file.id, file.file_name)}
              sx={{ bgcolor: "rgba(0,0,0,0.55)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              sx={{ bgcolor: "rgba(0,0,0,0.55)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            component="img"
            src={url}
            alt={file.file_name}
            sx={{ display: "block", maxWidth: "90vw", maxHeight: "85vh" }}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
